import {
  ANIMAL_STATUS_CODES,
  BREED_CODES,
  EXPENSE_CATEGORY_CODES,
  EXPENSE_SIDES,
  MEDIA_KINDS,
  MEDIA_STATUSES,
  PAYMENT_METHOD_CODES,
  PLATFORM_ROLE_CODES,
  ROLE_CODES,
  SEX_CODES,
  TEETH_AGE_CODES,
} from '@cattle/money'
import { z } from 'zod'

/**
 * Enums are **codes**, never labels: `DANT_4`, not "4 Dant". A label is a
 * translation waiting to happen; a code is data. Every user-facing string is
 * produced by `t()` on the client from the code in the payload.
 *
 * The domain vocabularies below are imported from `@cattle/money`, which owns
 * them. A code list is never retyped here — that is how the API and the two
 * frontends cannot drift apart on what a valid breed is.
 */
export const sexSchema = z.enum(SEX_CODES)
export const breedSchema = z.enum(BREED_CODES)
export const teethAgeSchema = z.enum(TEETH_AGE_CODES)
export const animalStatusSchema = z.enum(ANIMAL_STATUS_CODES)
export const expenseCategorySchema = z.enum(EXPENSE_CATEGORY_CODES)
export const expenseSideSchema = z.enum(EXPENSE_SIDES)
export const roleSchema = z.enum(ROLE_CODES)
export const platformRoleSchema = z.enum(PLATFORM_ROLE_CODES)
export const paymentMethodSchema = z.enum(PAYMENT_METHOD_CODES)
export const mediaKindSchema = z.enum(MEDIA_KINDS)
export const mediaStatusSchema = z.enum(MEDIA_STATUSES)

/**
 * The vocabularies below exist only at the API boundary — they are the codes
 * plan §3 gives to columns that `@cattle/money` has no reason to know about
 * (tenancy, billing, backups). They live here, once, for the same reason.
 */

/** `tenant_settings.currency_grouping`. Lakh/crore or thousands. */
export const CURRENCY_GROUPINGS = ['indian', 'western'] as const
export const currencyGroupingSchema = z.enum(CURRENCY_GROUPINGS)

/** `tenants.status` — the lifecycle in §4.18. */
export const TENANT_STATUSES = [
  'PENDING_VERIFICATION',
  'TRIAL',
  'ACTIVE',
  'PAST_DUE',
  'READ_ONLY',
  'SUSPENDED',
  'CANCELLED',
] as const
export const tenantStatusSchema = z.enum(TENANT_STATUSES)

export const PLAN_CODES = ['TRIAL', 'BASIC', 'TRADER', 'FARM'] as const
export const planCodeSchema = z.enum(PLAN_CODES)

/** `plans.features` — what a plan switches on (conflict C11: confirm). */
export const PLAN_FEATURES = [
  'OWNER_CONSOLE',
  'REPORTS',
  'PRICE_LOCK',
  'LOTS',
  'PARTNER_SHARES',
] as const
export const planFeatureSchema = z.enum(PLAN_FEATURES)

export const BILLING_PERIODS = ['MONTHLY'] as const
export const billingPeriodSchema = z.enum(BILLING_PERIODS)

export const SUBSCRIPTION_STATUSES = ['TRIALING', 'ACTIVE', 'PAST_DUE', 'CANCELLED'] as const
export const subscriptionStatusSchema = z.enum(SUBSCRIPTION_STATUSES)

export const INVOICE_STATUSES = ['ISSUED', 'PAID', 'FAILED', 'VOID'] as const
export const invoiceStatusSchema = z.enum(INVOICE_STATUSES)

/** How a *subscription* invoice was settled. Distinct from `PAYMENT_METHODS`, */
/** which is how a *buyer* paid the trader for an animal. */
export const INVOICE_METHODS = ['JAZZCASH', 'EASYPAISA', 'BANK', 'CARD'] as const
export const invoiceMethodSchema = z.enum(INVOICE_METHODS)

/** `lot_costs.split_method` (§4.13). Per head, or in proportion to price. */
export const SPLIT_METHODS = ['PER_HEAD', 'BY_PRICE'] as const
export const splitMethodSchema = z.enum(SPLIT_METHODS)

/** `recurring_expenses.mode` (§4.14). */
export const RECURRING_MODES = ['FIXED_AUTO', 'VARIABLE_CONFIRM'] as const
export const recurringModeSchema = z.enum(RECURRING_MODES)

export const POSTING_STATUSES = ['PENDING', 'POSTED', 'SKIPPED'] as const
export const postingStatusSchema = z.enum(POSTING_STATUSES)

/** How an animal left the herd other than by sale (§4.11). */
export const EXIT_REASONS = ['DIED', 'LOST'] as const
export const exitReasonSchema = z.enum(EXIT_REASONS)

/** An expense is either against one animal or shared across the herd. */
export const EXPENSE_SCOPES = ['animal', 'herd'] as const
export const expenseScopeSchema = z.enum(EXPENSE_SCOPES)

/** The renditions `GET /media/:id/:variant` serves. */
export const MEDIA_VARIANTS = ['original', 'display', 'thumb', 'poster', 'video'] as const
export const mediaVariantSchema = z.enum(MEDIA_VARIANTS)

export const BACKUP_KINDS = ['BOOK_ONLY', 'FULL', 'NIGHTLY', 'PRE_RESTORE'] as const
export const backupKindSchema = z.enum(BACKUP_KINDS)
/** What an owner may ask for. `NIGHTLY`/`PRE_RESTORE` are the system's to make. */
export const REQUESTABLE_BACKUP_KINDS = ['BOOK_ONLY', 'FULL'] as const
export const requestableBackupKindSchema = z.enum(REQUESTABLE_BACKUP_KINDS)

export const BACKUP_STATUSES = ['RUNNING', 'READY', 'FAILED'] as const
export const backupStatusSchema = z.enum(BACKUP_STATUSES)

/** The Owner Console's activity filter (design 04) and an entry's own kind. */
export const AUDIT_CATEGORIES = ['money', 'people', 'deleted'] as const
export const auditCategorySchema = z.enum(AUDIT_CATEGORIES)
export const AUDIT_KINDS = ['MONEY', 'PEOPLE', 'DELETED', 'OTHER'] as const
export const auditKindSchema = z.enum(AUDIT_KINDS)

/** Who made the change — a user of the book, or a platform actor inside it. */
export const ACTOR_KINDS = ['TENANT_USER', 'PLATFORM'] as const
export const actorKindSchema = z.enum(ACTOR_KINDS)

/** What the 90-day trash can hold, and what `/trash/:entity/:id/restore` takes. */
export const TRASH_ENTITIES = [
  'animal',
  'expense',
  'sale',
  'payment',
  'party',
  'lot',
  'feedPlan',
  'recurringExpense',
  'media',
] as const
export const trashEntitySchema = z.enum(TRASH_ENTITIES)

export const TICKET_SOURCES = ['WHATSAPP', 'PHONE', 'EMAIL', 'IN_APP'] as const
export const ticketSourceSchema = z.enum(TICKET_SOURCES)
export const TICKET_STATUSES = ['OPEN', 'ANSWERED', 'CLOSED'] as const
export const ticketStatusSchema = z.enum(TICKET_STATUSES)
export const MESSAGE_DIRECTIONS = ['IN', 'OUT'] as const
export const messageDirectionSchema = z.enum(MESSAGE_DIRECTIONS)

/** `access_log.mode` — a platform actor is reading, or has switched to edit. */
export const ACCESS_MODES = ['READ_ONLY', 'EDIT'] as const
export const accessModeSchema = z.enum(ACCESS_MODES)

/** How the session token travels: web cookie, or mobile bearer. */
export const SESSION_TRANSPORTS = ['COOKIE', 'BEARER'] as const
export const sessionTransportSchema = z.enum(SESSION_TRANSPORTS)

export const OTP_PURPOSES = ['SIGNUP', 'PASSWORD_RESET'] as const
export const otpPurposeSchema = z.enum(OTP_PURPOSES)

/**
 * The capability vocabulary of §5.4. The UI shows or hides from this list; the
 * server enforces the same rules independently. **A capability missing from
 * the token never means the server may skip its own check** — the list is a
 * rendering hint, not an authorisation decision.
 */
export const CAPABILITIES = [
  'animal.read',
  'animal.write',
  'animal.delete',
  'expense.write',
  'money.read',
  'sale.record',
  'lot.write',
  'feed.manage',
  'report.read',
  'people.manage',
  'settings.manage',
  'backup.manage',
  'audit.read',
  'trash.manage',
] as const
export const capabilitySchema = z.enum(CAPABILITIES)
export type Capability = z.infer<typeof capabilitySchema>
